import '@gl/lit/components/base/avatar/index.js';
import '@gl/lit/components/base/input/index.js';
import '@gl/lit/components/base/dropdown_v2/index.js';
import '@gl/lit/components/base/list/index.js';
import '@gl/lit/components/base/list_item/index.js';
import '@gl/lit/components/base/icon/index.js';
import '@gl/lit/components/base/badge/index.js';

import {html, nothing} from 'lit';
import {repeat} from 'lit/directives/repeat.js';
import {choose} from 'lit/directives/choose.js';
import {RequestMethod} from '@gl/lit/common/constants/index.js';
import type {CustomInputEvent} from '@gl/lit/components/base/input/index.js';
import type {
  Cmd,
  ImmutableProgram,
  InitialProperties,
  ImmutableResult,
  UpdateHandler,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import {
  Variant,
  makeVariants,
} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {getCSRFToken} from '@gl/lit/utils/auth.js';
import {fetcher} from '@gl/lit/utils/http.js';
import {renderErrorNotification} from '@gl/lit/utils/notifications.js';
import type {SessionContextData} from '@gl/lit/contexts/session.js';
import type {VariantsFor} from '@gl/lit/types/architecture.js';
import type Impersonator from './index.js';
import {Size} from '@gl/lit/components/base/icon/index.js';
import type {
  ImpersonationOptions,
  RankedImpersonationOptionData,
} from './utils.js';
import {SortBy, createSearchEngine} from './utils.js';
import {when} from 'lit/directives/when.js';
import {debounce} from '@gl/utils/debounce.js';
import type {
  Computed,
  ComputedModel,
} from '@gl/lit/components/mixins/immutable_component/computed.js';
import {createModel} from '@gl/lit/components/mixins/immutable_component/computed.js';

enum Dropdowns {
  Primary,
  Sort,
}

export type Model = {
  query: string;
  session: Nullable<SessionContextData>;
  options: ImpersonationOptions;
  sortBy: SortBy;
  open: Set<Dropdowns>;
  searchEngine: ReturnType<typeof createSearchEngine>;
  filteredOptions: Computed<RankedImpersonationOptionData[]>;
};

export type CModel = ComputedModel<Model>;

export const Msg = makeVariants({
  setSession: Variant<Nullable<SessionContextData>>()('set-session'),
  setQuery: Variant<string>()('set-query'),
  setSortBy: Variant<SortBy>()('set-sort-by'),
  impersonate: Variant<{id: number; teamId: number}>()('impersonate'),
  exit: Variant<number>()('exit'),
  open: Variant<Dropdowns>()('open'),
  close: Variant<Dropdowns>()('close'),
});

export type Msg = VariantsFor<typeof Msg>;

// -- Init

function init(props: InitialProperties) {
  const session = <Nullable<SessionContextData>>props.get('session');
  const list = <Model['options']>props.get('options') ?? [];
  const searchEngine = createSearchEngine(list);

  const model = createModel<Model>(compute => ({
    query: '',
    sortBy: SortBy.Org,
    session: session ?? null,
    options: list,
    open: new Set(),
    searchEngine,
    filteredOptions: compute(model =>
      model.searchEngine.search(model.query, model.sortBy)
    ),
  }));

  return model;
}

// -- Update

async function impersonate(id: number, teamId: number) {
  try {
    const URL = `/admin/impersonations/${id}`;

    const body = new FormData();
    body.append('_method', 'patch');
    body.append('authenticity_token', getCSRFToken());
    body.append('team_id', teamId.toString());
    const redirectUrl = await fetcher<string>(URL, {
      method: RequestMethod.POST,
      body,
      responseParser: 'url',
    });

    location.replace(redirectUrl);
  } catch (err) {
    if (err instanceof Error) renderErrorNotification(err);
  }
}

async function exit(id: number) {
  try {
    const URL = `/admin/impersonations/${id}`;

    const body = new FormData();
    body.append('_method', 'delete');
    body.append('authenticity_token', getCSRFToken());

    const redirectUrl = await fetcher<string>(URL, {
      method: RequestMethod.POST,
      body,
      responseParser: 'url',
    });

    location.replace(redirectUrl);
  } catch (err) {
    if (err instanceof Error) renderErrorNotification(err);
  }
}

function update(msg: Msg, model: CModel, cmd: Cmd): ImmutableResult<CModel> {
  switch (msg.tag) {
    case 'set-query':
      return {...model, query: msg.value} as const;
    case 'set-session':
      return {...model, session: msg.value} as const;
    case 'open':
      return {...model, open: model.open.add(msg.value)} as const;
    case 'close': {
      model.open.delete(msg.value);
      return {...model} as const;
    }
    case 'impersonate':
      return [model, cmd(() => impersonate(msg.value.id, msg.value.teamId))];
    case 'set-sort-by':
      return [
        {...model, sortBy: msg.value},
        cmd((h: Impersonator) => h.handleUpdate(Msg.close(Dropdowns.Sort))),
      ];
    case 'exit':
      return [model, cmd(() => exit(msg.value))];
  }
}

// -- View

function userRoleToVariant(role: string): string {
  switch (role) {
    case 'admin':
      return 'red--light';
    case 'owner':
      return 'primary--light';
    default:
      return 'secondary--light';
  }
}

function viewSortBy(
  model: CModel,
  handleUpdate: UpdateHandler<Model, Msg>,
  numberOfItems: number
) {
  return html`
    <div class="impersonator__sort" slot="content">
      <div class="impersonator__sort__label">Sort By</div>
      <fly-dropdown-v2
        .open=${model.open.has(Dropdowns.Sort)}
        optionsPlacement="top-start"
        closeWhenClickOutside
        updateOnAnimationFrame
        @open=${() => handleUpdate(Msg.open(Dropdowns.Sort))}
        @close=${(e: Event) => {
          e.stopImmediatePropagation();
          handleUpdate(Msg.close(Dropdowns.Sort));
        }}
      >
        <div class="impersonator__sort__anchor" slot="anchor">
          ${choose(model.sortBy, [
            [SortBy.Name, () => 'Name'],
            [SortBy.Org, () => 'Organization'],
          ])}
          <fly-icon icon="keyboard_arrow_down"></fly-icon>
        </div>
        <div class="impersonator__sort__content" slot="content">
          <fly-list singleSelect>
            <fly-list-item
              ?selected=${model.sortBy === SortBy.Name}
              .value=${SortBy.Name}
              @selected=${() => handleUpdate(Msg.setSortBy(SortBy.Name))}
            >
              Name
            </fly-list-item>
            <fly-list-item
              ?selected=${model.sortBy === SortBy.Org}
              .value=${SortBy.Org}
              @selected=${() => handleUpdate(Msg.setSortBy(SortBy.Org))}
            >
              Org
            </fly-list-item>
          </fly-list>
        </div>
      </fly-dropdown-v2>

      <div class="impersonator__sort__items-count">
        ${numberOfItems}/${model.options.length}
      </div>
    </div>
  `;
}

function viewOptions(
  model: CModel,
  options: RankedImpersonationOptionData[],
  handleUpdate: UpdateHandler<Model, Msg>
) {
  const debouncedSetQuery = debounce((e: CustomEvent<CustomInputEvent>) => {
    handleUpdate(Msg.setQuery(e.detail.value));
  }, 250);

  return html`
    <div class="impersonator__options" slot="content">
      <div class="impersonator__options__header">
        <fly-input
          hasicon
          type="search"
          placeholder="Search"
          variant="borderless-no-bg"
          @input=${(e: CustomEvent<CustomInputEvent>) => debouncedSetQuery(e)}
        >
          <div slot="icon">
            <fly-icon icon="search"></fly-icon>
          </div>
        </fly-input>
        ${viewSortBy(model, handleUpdate, options.length)}
      </div>
      <div class="impersonator__options__items">
        <fly-list .singleSelect=${true}>
          ${repeat(
            options,
            data => data.item.id,
            (data, idx) => html`
              ${when(
                model.sortBy === SortBy.Org &&
                  (idx === 0 || data.item.org !== options[idx - 1]?.item.org),
                () =>
                  html`<p class="impersonator__org-title">${data.item.org}</p>`
              )}
              <fly-list-item
                .value=${String(data.item.id)}
                @selected=${() => {
                  handleUpdate(
                    Msg.impersonate({
                      id: data.item.id,
                      teamId: data.item.team_id,
                    })
                  );
                }}
              >
                <div class="impersonator__option">
                  <div class="impersonator__option__header">
                    <span
                      >${data.item.nickname ??
                      data.item.name ??
                      data.item.email}</span
                    >
                    <fly-badge
                      variant="${userRoleToVariant(data.item.role)}"
                      size="xs"
                      >${data.item.role}</fly-badge
                    >
                  </div>
                  <div class="impersonator__option__body">
                    <div class="impersonator__metadatum">
                      <fly-avatar
                        circle
                        randomize
                        .name=${data.item.org}
                        size="12"
                      ></fly-avatar>
                      <span>${data.item.org}</span>
                    </div>
                    <div class="impersonator__metadatum">
                      <fly-icon size=${Size.XS} icon="groups"></fly-icon>
                      <span>${data.item.team ?? '-'}</span>
                    </div>
                    <div class="impersonator__metadatum">
                      <fly-icon size=${Size.XS} icon="email"></fly-icon>
                      ${data.item.email ?? '-'}
                    </div>
                  </div>
                </div>
              </fly-list-item>
            `
          )}
        </fly-list>
      </div>
    </div>
  `;
}

function view(model: CModel, handleUpdate: UpdateHandler<Model, Msg>) {
  const name = model.session?.user()?.nickname ?? model.session?.user()?.name;
  const id = model.session?.user()?.id;
  if (!name || !id) return nothing;

  return html`
    <div class="impersonator">
      <div class="impersonator__content">
        You are impersonating

        <fly-dropdown-v2
          .open=${model.open.has(Dropdowns.Primary)}
          optionsPlacement="top-end"
          closeWhenClickOutside
          updateOnAnimationFrame
          @open=${() => handleUpdate(Msg.open(Dropdowns.Primary))}
          @close=${() => handleUpdate(Msg.close(Dropdowns.Primary))}
        >
          <div class="impersonator__info" slot="anchor">
            <fly-avatar randomize .name=${name} circle size="16"></fly-avatar>
            <span class="impersonator__user">${name}</span>
            <fly-icon icon="keyboard_arrow_down"></fly-icon>
          </div>
          ${viewOptions(model, model.filteredOptions, handleUpdate)}
        </fly-dropdown-v2>
        <div class="exit" @click=${() => handleUpdate(Msg.exit(id))}>Exit</div>
      </div>
      <slot></slot>
    </div>
  `;
}

// -- Program

export var main: ImmutableProgram<Model, Msg> = {
  init,
  update,
  view,
};
